import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmSignUp, fetchUserAttributes, signIn, getCurrentUser, fetchAuthSession, resendSignUpCode, signOut } from 'aws-amplify/auth';
import VendorNav from '../Homepage/Components/VendorNav';
import { useUser } from '../../UserContext';
import { LoginBackground } from '../../Constants/Backgrounds';
import { orangeButtonCentered } from '../../Constants/Styles';
import { PasswordTurnVisibleIcon, PasswordTurnInvisibleIcon } from '../../Constants/Icons';
import { ToastContainer } from 'react-toastify';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useToast } from '../../Common/ToastContext';
import { formTextboxLabel } from "../../Constants/Styles";

function VendorValidate() {
    const showToast = useToast();
    const navigate = useNavigate();
    const { setUser, setIsSignedIn } = useUser();
    const [resendLoading, setResendLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [authenticationCode, setAuthenticationCode] = useState('');
    const [password, setPassword] = useState('');
    const [formData, setFormData] = useState({
        vendorName: '',
        description: '',
        address: '',
        phoneNumber: '',
        website: '',
        yearFounded: '',
        numberOfEmployees: '',
        revenue: '',
        contactName: '',
        title: '',
        email: '',
        serviceSpecialties: '',
        serviceDescription: '',
        servicePlanDescription: '',
        interestedInAPI: false,
    });

    const [loginLoading, setLoginLoading] = useState(false);
    const [initialSetupLoading, setInitialSetupLoading] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const togglePasswordVisibility = () => {
        setIsPasswordVisible(!isPasswordVisible);
    };

    async function signOutUser() {
        try {
            await signOut();
            setIsSignedIn(false);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    }


    async function handleSignUpConfirmation(event) {
        event.preventDefault();

        if (!email || !authenticationCode || !password) {
            showToast('Error: Please fill out every field');
            return;
        }

        setLoginLoading(true);

        try {

            const { isSignUpComplete } = await confirmSignUp({
                username: email,
                confirmationCode: authenticationCode,
            });

            if (!isSignUpComplete) {
                throw new Error('Sign-up confirmation failed.');
            }

            // Sign in the user
            await signIn({ username: email, password: password });

            setLoginLoading(false);
            setIsSignedIn(true);
            navigate('/vendors/loading');

        } catch (error) {
            // Sign out the user if critical steps fail
            await signOutUser();

            setLoginLoading(false);
            if (error.name === 'UserNotFoundException') {
                showToast('Error','Error: User not found. Please check your email and password and try again.','error');
            } else if (error.name === 'CodeMismatchException') {
                showToast('Error','Error: Your confirmation code is incorrect. Please try again.','error');
            } else if (error.name === 'TooManyFailedAttemptsException') {
                showToast('Error','Error: You have made too many failed attempts. Please try again later.','error');
            } else if (error.name === 'TooManyRequestsException') {
                showToast('Error','Error: You have made too many requests. Please try again later.','error');
            }
            else if (error.name === 'NotAuthorizedException' && error.message === 'User cannot be confirmed. Current status is CONFIRMED') {
                showToast('Error','Error: Your account is already confirmed.','error');
                try {
                    await signIn({ username: email, password: password });
                    setIsSignedIn(true);
                    navigate('/vendors/loading');
                }
                catch (error) {
                    navigate('/vendors/login');
                }
            }
            else if (error.name === 'NotAuthorizedException') {
                showToast('Error','Error: Incorrect username or password.', 'error');
            } else {
                showToast('Error','Sorry, there appears to be an issue. Please try again later.', 'error');
            }
        }
    }

    async function handleResendCode() {

        if (!email) {
            showToast('Error: Please enter your email first.');
            return;
        }

        setResendLoading(true);

        try {
            await resendSignUpCode({ username: email });
            showToast('Success','Verification code resent successfully.', 'success');
        } catch (error) {
            // Handle specific errors if necessary
            if (error.name === 'UserNotFoundException') {
                showToast('Error: No account found with this email.');
            } else if (error.name === 'LimitExceededException') {
                showToast('Error: Attempt limit exceeded. Please try again later.');
            } else if (error.name === 'InvalidParameterException' && error.message === 'User is already confirmed.') {
                showToast('Error: Your account is already confirmed. Please sign in.');
                navigate('/vendors/login');
            }
            else {
                showToast(`Error resending verification code: ${error.message}`);
            }
        } finally {
            setResendLoading(false);
        }
    }


    return (
        <div className="flex flex-col h-screen">
            <VendorNav navbarColor="#26051C" />
            <ToastContainer />
            <div
                className="w-full h-full flex justify-center items-center p-4"
                style={{
                    backgroundImage: `url(${LoginBackground})`,
                    backgroundSize: 'cover',
                    position: 'relative',
                }}
            >
                <div className="absolute inset-0 bg-black opacity-5 z-0" />
                <div className="sm:mt-12 w-full xs:w-[32rem] bg-white rounded-lg shadow-lg flex flex-col z-50 ">
                    <div className="px-16 py-10">
                        <h2 className="mt-8 font-proximanova text-[26px] font-[600] text-[#505050] text-center mb-0">Please verify your email</h2>
                        <h3 className="font-proximanova text-[18px] text-[#505050] text-center sm:text-left mb-0">to continue signing in</h3>

                        <div className="mt-4 flex flex-col w-full">
                            <label className={formTextboxLabel}>
                                Email
                            </label>
                            <input
                                type="email"
                                className="block py-2 px-4 w-full border border-[#505050] font-proximanova text-[18px] appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>

                        <div className="mt-4 flex flex-col w-full">
                            <label className={formTextboxLabel}>
                                Confirmation Code
                            </label>
                            <input
                                className="block py-2 px-4 w-full border border-[#505050] font-proximanova text-[18px] appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                placeholder="Confirmation Code"
                                required
                                value={authenticationCode}
                                onChange={(e) => setAuthenticationCode(e.target.value)}
                            />
                        </div>

                        <div className="mt-6 flex">
                            <button
                                className="w-fit h-fit mx-auto text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4"
                                onClick={handleResendCode}
                                disabled={resendLoading}
                            >
                                {(resendLoading) ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm mr-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Resending...
                                    </>
                                ) : (
                                    'Resend Code'
                                )}
                            </button>
                        </div>

                        <div className="mt-4 flex flex-col w-full">
                            <label className={formTextboxLabel}>
                                Password
                            </label>
                            <div className="flex flex-row items-center relative">
                                <input
                                    type={isPasswordVisible ? 'text' : 'password'}
                                    className="block py-2.5 px-4 w-full text-base text-gray-900 border border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <button
                                    type="button"
                                    onClick={togglePasswordVisibility}
                                    className="absolute right-0 mr-4"
                                >
                                    {isPasswordVisible ? (
                                        <PasswordTurnVisibleIcon />
                                    ) : (
                                        <PasswordTurnInvisibleIcon />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="mt-6 flex">
                            <button
                                className="w-fit h-fit mx-auto text-white bg-link hover:text-integrationRequestTextHover hover:bg-charmHover rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4"
                                onClick={handleSignUpConfirmation}
                                disabled={loginLoading}
                            >
                                {(loginLoading) ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm mr-2"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Confirming...
                                    </>
                                ) : (
                                    'Confirm Account'
                                )}
                            </button>
                        </div>
                        {initialSetupLoading && (
                            <p className="mt-4 text-center font-proximanova text-[18px] text-[#505050]">
                                Login complete! Please wait while we finish your initial account setup.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default VendorValidate;
